import { useState, useCallback } from "react";
import { toast } from "sonner";
import { User } from "@/types/User";
import axios, { AxiosError } from "axios";
import { getApiEndpoint, fileUpload } from "@/utils/ERPUtils";

import { logger } from '@/utils/logger';
export interface EditUserFormData {
  email: string;
  first_name: string;
  last_name: string;
  middle_name?: string;
  username: string;
  timezone: string;
  roles: string[];
  avatar?: string;
  gender?: string;
  birthdate?: string;
  interests?: string;
  location?: string;
  job_role?: string;
  phone?: string;
  mobile_number?: string;
  department?: string;
  employee_id?: string;
  hire_date?: string;
  bio?: string;
}

interface ApiResponse<T = any> {
  message: {
    status: "success" | "error";
    message?: string;
    data?: T;
  };
}

/**
 * Formats a date string (YYYY-MM-DD) into ISO date for backend.
 */
const formatDate = (date?: string): string | null => {
  if (!date) return null;
  return new Date(date).toISOString().split("T")[0];
};

/**
 * Extracts a readable error message from Axios or generic errors.
 */
const getErrorMessage = (error: unknown): string => {
  if (axios.isAxiosError(error)) {
    return (
      (error.response?.data as any)?.message?.message ||
      error.response?.statusText ||
      error.message
    );
  }
  if (error instanceof Error) return error.message;
  return "An unknown error occurred";
};

export const useEditUserForm = () => {
  const [loading, setLoading] = useState(false);

  /**
   * Updates the user profile via backend API.
   */
  const updateUserProfile = useCallback(async (
    data: EditUserFormData,
    isCurrentUser = false,
  ): Promise<void> => {
    // Backend update_user_details destructively replaces ALL roles with the
    // single `role` value. Sending it from a self-service profile save would
    // wipe the user's other roles (e.g. an Admin loses 'System Manager').
    // Only the admin user-management flow should mutate roles.
    const payload: Record<string, any> = {
        email: data.email,
        first_name: data.first_name,
        last_name: data.last_name,
        middle_name: data.middle_name,
        username: data.username,
        timezone: data.timezone,
        user_image: data.avatar,
        gender: data.gender,
        birth_date: data.birthdate ? new Date(data.birthdate).toISOString().split('T')[0] : null,
        interest: data.interests,
        location: data.location,
        job_role: data.job_role || null,
        phone: data.phone,
        mobile_no: data.mobile_number,
        department: data.department,
        employee_id: data.employee_id,
        bio: data.bio,
        name: `${data.first_name} ${data.last_name}`,
        updated_at: new Date().toISOString(),
    };
    if (!isCurrentUser) {
      payload.role = data?.roles?.[0];
    }

    try {
      // Update user details
      const response = await axios.post<ApiResponse>(
        getApiEndpoint("EDIT_USER"),
        payload
      );

      if (response.data.message.status === "error") {
        throw new Error(response.data.message.message || "User update failed");
      }

      // Update avatar only if user details update succeeded
      if (data?.avatar && data?.email) {
        try {
          const avatarResponse = await axios.post(
            getApiEndpoint("UPDATE_USER_AVATAR"),
            {
              file_url: data.avatar,
              email: data.email,
            }
          );

          if (avatarResponse.data?.message?.status === "error") {
            logger.warn("Avatar update failed:", avatarResponse.data?.message?.message);
          }
        } catch (avatarError) {
          logger.error("Avatar upload request failed:", avatarError);
        }
      }
    } catch (error) {
      logger.error("User update failed:", error);
      throw error; // rethrow so calling code knows it failed
    }

  }, []);

  /**
   * Upload avatar file if selected
   */
  const uploadAvatarFile = useCallback(async (file: File): Promise<string | null> => {
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return null;
    }

    try {
      const result = await fileUpload(file, 2, { folderToBeUploaded: 'user_profile_images' });
      if (result.error) {
        throw new Error(result.error);
      }
      return result.url || null;
    } catch (error) {
      logger.error('Error uploading avatar:', error);
      toast.error('Failed to upload profile image');
      return null;
    }
  }, []);

  /**
   * Handles form submission, updates user, shows toasts, and triggers callbacks.
   */
  const submitForm = useCallback(
    async (
      _user: User, // kept for compatibility, but unused
      data: EditUserFormData,
      onSuccess: () => void,
      onCancel: () => void,
      avatarFile?: File | null,
      isCurrentUser = false,
    ): Promise<void> => {
      setLoading(true);
      try {
        let finalData = { ...data };

        if (avatarFile) {
          const avatarUrl = await uploadAvatarFile(avatarFile);
          if (avatarUrl) {
            finalData.avatar = avatarUrl;
            toast.success('Profile image uploaded successfully');
          } else {
            // If avatar upload fails, we can still proceed with other data
            toast.warning('Avatar upload failed, but proceeding with other updates');
          }
        } else if (finalData.avatar) {
          delete finalData.avatar;
        }

        await updateUserProfile(finalData, isCurrentUser);
        toast.success("User updated successfully");
        onSuccess();
        onCancel();
      } catch (error) {
        toast.error(getErrorMessage(error));
      } finally {
        setLoading(false);
      }
    },
    [updateUserProfile, uploadAvatarFile]
  );

  /**
   * Returns initials from first and last name.
   */
  const getInitials = useCallback(
    (firstName: string, lastName: string): string =>
      `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase(),
    []
  );

  return {
    loading,
    submitForm,
    getInitials,
  };
};
